import { useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { AirSettingsBusinessAddressSource } from '@frontend/api-ai-settings';
import { stateOptions, CountryCodes } from '@frontend/geography';
import { useTranslation } from '@frontend/i18n';
import { DropdownField, Text, TextField, PostalCodeField, FieldProps } from '@frontend/design-system';
import { BusinessAddressSourceValue } from '../../../../types/air-settings.types';
import { BulkOverrideWarningAlert } from '../../common/bulk-override-warning-alert';
import { ContextualDataAlert } from '../../common/contextual-data-alert';
import { KnowledgeSourceInput } from '../../common/knowledge-source-input';
import { BusinessAddressSourceLabelMap, BusinessAddressSourceLinkMap } from '../constants';
import { BusinessKnowledgeFormFields } from './use-business-knowledge-form';

const DEFAULT_SOURCES: BusinessAddressSourceValue[] = [
  { source: 'ORGANIZATION_SETTINGS', addressLine1: '', addressLine2: '', city: '', state: '', postalCode: '' },
  { source: 'BRAND', addressLine1: '', addressLine2: '', city: '', state: '', postalCode: '' },
];

const hasVisibleSourceData = ({
  source,
  addressLine1,
  addressLine2,
  city,
  state,
  postalCode,
}: BusinessAddressSourceValue) => {
  return source === 'CUSTOM' || !!addressLine1 || !!addressLine2 || !!city || !!state || !!postalCode;
};

type Props = {
  getFieldProps: <K extends keyof BusinessKnowledgeFormFields>(name: K) => FieldProps<BusinessKnowledgeFormFields, K>;
  sourceList?: BusinessAddressSourceValue[];
  isBulkEdit?: boolean;
  showBulkOverrideWarningAlert?: boolean;
};

export const BusinessAddressInput = ({
  getFieldProps,
  sourceList = DEFAULT_SOURCES,
  isBulkEdit = false,
  showBulkOverrideWarningAlert = false,
}: Props) => {
  const { t } = useTranslation('ai-receptionist-settings');

  const sourceFieldProps = getFieldProps('businessAddressSource');

  const options = useMemo(() => {
    const listWithCustom = sourceList.some((s) => s.source === 'CUSTOM')
      ? sourceList
      : [...sourceList, { source: 'CUSTOM' } as BusinessAddressSourceValue];

    return (
      listWithCustom
        // Skip visible sources check if in bulk edit mode, since they will be different for each location
        .filter((source) => isBulkEdit || hasVisibleSourceData(source))
        .map(({ source }) => {
          const src = source as AirSettingsBusinessAddressSource;
          return {
            value: src,
            label: BusinessAddressSourceLabelMap[src],
            settingsPageLink: BusinessAddressSourceLinkMap[src],
          };
        })
    );
  }, [sourceList, isBulkEdit]);

  const renderSourceData = useCallback(
    (source: AirSettingsBusinessAddressSource) => {
      if (!source) return null;

      if (source === 'CUSTOM') {
        return (
          <>
            <div css={styles.customInputWrapper}>
              <TextField
                {...getFieldProps('addressLine1')}
                label=''
                placeholder={t('Address line 1')}
                containerCss={styles.fullWidthField}
              />
              <TextField
                {...getFieldProps('addressLine2')}
                label=''
                placeholder={t('Address line 2')}
                containerCss={styles.fullWidthField}
              />
              <div css={styles.rowWrapper}>
                <TextField
                  {...getFieldProps('city')}
                  label=''
                  placeholder={t('City')}
                  containerCss={styles.cityField}
                />
                <DropdownField {...getFieldProps('state')} label='' placeholder={t('State')}>
                  {stateOptions[CountryCodes.USA]?.map(({ label, value }) => (
                    <DropdownField.Option key={value} value={value} searchValue={label}>
                      {label}
                    </DropdownField.Option>
                  ))}
                </DropdownField>
              </div>
              <PostalCodeField
                {...getFieldProps('postalCode')}
                label=''
                placeholder={t('Postal Code')}
                containerCss={styles.postalCodeField}
              />
            </div>
            {showBulkOverrideWarningAlert && <BulkOverrideWarningAlert />}
          </>
        );
      } else if (isBulkEdit) {
        return <ContextualDataAlert />;
      } else {
        const selectedSourceData = sourceList.find((ns) => ns.source === source);
        if (!selectedSourceData) return null;

        const { addressLine1, addressLine2, city, state, postalCode } = selectedSourceData;

        return (
          <div css={styles.displayValueBox}>
            {addressLine1 && <Text size='medium'>{addressLine1}</Text>}
            {addressLine2 && <Text size='medium'>{addressLine2}</Text>}
            {(city || state) && <Text size='medium'>{[city, state].filter(Boolean).join(', ')}</Text>}
            {postalCode && <Text size='medium'>{`${postalCode} USA`}</Text>}
          </div>
        );
      }
    },
    [isBulkEdit, getFieldProps, t, showBulkOverrideWarningAlert, sourceList]
  );

  return (
    <KnowledgeSourceInput<any, any, AirSettingsBusinessAddressSource>
      fieldProps={sourceFieldProps as any}
      options={options}
      renderSourceData={renderSourceData}
    />
  );
};

const styles = {
  customInputWrapper: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-sm);
    padding: var(--fabric-space-padding-xs) var(--fabric-space-padding-xs);
    border-radius: var(--fabric-radius-xs);
    background-color: var(--fabric-color-surface-default);
  `,
  rowWrapper: css`
    display: grid;
    grid-template-columns: 1.5fr 1fr;
    gap: var(--fabric-space-gap-sm);
  `,
  fullWidthField: css`
    width: 100%;
    margin-bottom: 0;
  `,
  cityField: css`
    flex: 1.5;
    margin-bottom: 0;
  `,
  stateField: css`
    flex: 1;
    margin-bottom: 0;
  `,
  postalCodeField: css`
    width: calc(50% - var(--fabric-space-gap-sm) / 2);
    margin-bottom: 0;
  `,
  displayValueBox: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-xxs);
    padding: var(--fabric-space-padding-sm) var(--fabric-space-padding-md);
    border-radius: var(--fabric-radius-sm);
    background-color: var(--fabric-color-surface-default);
  `,
};
